/**
 * "Which destination was that?" for delivery rows. The timeline and the
 * test-lead screen only carry `destination_id`; without a name, a partner
 * with an email and a webhook cannot tell which one failed.
 */
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import type { Destination } from '../api/types';
import { useQuery } from '../ui/hooks';

export function destinationLabel(d: Destination): string {
  if (d.kind === 'webhook' && typeof d.config.url === 'string') {
    try {
      return `webhook ${new URL(d.config.url).host}`;
    } catch {
      return 'webhook';
    }
  }
  const to = d.config.recipients ?? [];
  if (d.kind === 'email' && to.length) return `email ${to[0]}${to.length > 1 ? ` +${to.length - 1}` : ''}`;
  return d.kind;
}

/** Resolves a destination id to its label; '' until loaded or if unknown. */
export function useDestinationLabels(): (id: unknown) => string {
  const find = useDestinationLookup();
  return (id) => {
    const d = find(id);
    return d ? destinationLabel(d) : '';
  };
}

/**
 * Resolves a destination id to the destination; null until loaded, if
 * unknown, or for a role that may not read destinations (an operator reading
 * a lead), which is asked nothing.
 */
export function useDestinationLookup(): (id: unknown) => Destination | null {
  const { api, role } = useSession();
  const list = useQuery(() => api.listDestinations(), [], { enabled: can(role, 'destinations') });
  const byId = new Map((list.data?.items ?? []).map((d) => [d.id, d]));
  return (id) => (typeof id === 'string' ? (byId.get(id) ?? null) : null);
}
